

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/base/Button';
import SEO from '../../components/SEO';

export default function DaaS() {
  const [selectedPackage, setSelectedPackage] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [currentQuestion, setCurrentQuestion] = useState(0);

  const questions = [
    "What if schools could transform without breaking budgets?",
    "Why should technology be a luxury in education?",
    "How do we prepare students for jobs that don't exist yet?",
    "What if every classroom could be a portal to the future?"
  ];

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuestion((prev) => (prev + 1) % questions.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const packages = [
    {
      name: "Revolutionary Package",
      price: "₦50,000",
      period: "per student/year",
      description: "Complete digital transformation for forward-thinking schools",
      intensity: "Game-Changing",
      features: [
        "25-30 Interactive Smartboards",
        "VR Learning Kits",
        "3D Printers for STEM",
        "50 High-Performance Desktops",
        "Complete Science Lab Upgrades",
        "Teacher Laptops & Training",
        "4IR Curriculum Integration",
        "24/7 Technical Support",
        "Cloud-based Learning Management",
        "Regular Software Updates"
      ],
      highlight: true
    },
    {
      name: "Transformation Package",
      price: "₦30,000",
      period: "per student/year",
      description: "Essential technology infrastructure for growing schools",
      intensity: "Mind-Bending",
      features: [
        "15-24 Interactive Smartboards",
        "VR Learning Kits",
        "30 Student Desktops",
        "Tablets for Teachers",
        "Basic Lab Equipment Upgrades",
        "4IR Training Program",
        "Technical Support",
        "Learning Management System",
        "Software Maintenance",
        "Monthly Progress Reports"
      ],
      highlight: false
    }
  ];

  const benefits = [
    {
      icon: "ri-money-dollar-circle-line",
      title: "Revolutionary Economics",
      description: "No massive upfront investment. Transform as you scale.",
      color: "from-emerald-500 to-teal-600"
    },
    {
      icon: "ri-tools-line",
      title: "Total Domination Support",
      description: "Complete maintenance and technical mastery included.",
      color: "from-blue-500 to-indigo-600"
    },
    {
      icon: "ri-graduation-cap-line",
      title: "Teacher Revolution",
      description: "Comprehensive 4IR training that transforms educators.",
      color: "from-purple-500 to-pink-600"
    },
    {
      icon: "ri-cloud-line",
      title: "Limitless Integration",
      description: "Access learning materials anywhere, anytime, any device.",
      color: "from-cyan-500 to-blue-600"
    },
    {
      icon: "ri-shield-check-line",
      title: "Future-Proof Mastery",
      description: "Regular updates keep your technology ahead of the curve.",
      color: "from-orange-500 to-red-600"
    },
    {
      icon: "ri-team-line",
      title: "Elite Expert Team",
      description: "Dedicated support from education technology revolutionaries.",
      color: "from-green-500 to-emerald-600"
    }
  ];

  const schools = [
    {
      name: "Greenfield International School",
      location: "Abuja",
      students: "500+",
      testimonial: "TheSpire's DaaS completely revolutionized our learning universe.",
      transformation: "300% increase in student engagement",
      image: "https://readdy.ai/api/search-image?query=Modern%20African%20school%20classroom%20with%20students%20using%20tablets%20and%20smartboards%2C%20teacher%20demonstrating%20technology%2C%20bright%20educational%20environment%2C%20diverse%20Black%20students%20engaged%20with%20digital%20learning%20tools%2C%20professional%20educational%20photography&width=400&height=300&seq=9&orientation=landscape"
    },
    {
      name: "Future Leaders Academy",
      location: "Abuja",
      students: "800+",
      testimonial: "Our students are now architects of the digital future.",
      transformation: "500% improvement in tech literacy",
      image: "https://readdy.ai/api/search-image?query=African%20students%20working%20collaboratively%20with%20VR%20headsets%20and%203D%20printers%20in%20modern%20STEM%20classroom%2C%20innovative%20educational%20technology%2C%20bright%20learning%20environment%2C%20engaged%20Black%20students%2C%20professional%20educational%20setting&width=400&height=300&seq=10&orientation=landscape"
    },
    {
      name: "Excellence Private School",
      location: "Port Harcourt",
      students: "300+",
      testimonial: "The support and training exceeded every expectation we had.",
      transformation: "400% boost in teacher confidence",
      image: "https://readdy.ai/api/search-image?query=African%20teacher%20using%20interactive%20smartboard%20with%20engaged%20students%20in%20modern%20classroom%2C%20educational%20technology%20demonstration%2C%20bright%20learning%20environment%2C%20diverse%20Black%20students%20and%20teacher%2C%20professional%20educational%20photography&width=400&height=300&seq=11&orientation=landscape"
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "TheSpire Device-as-a-Service (DaaS)",
    "description": "Revolutionize learning with our DaaS model. Schools get complete tech infrastructure with support, paying per student without large upfront investment.",
    "url": `${import.meta.env.VITE_SITE_URL || "https://thespire.com.ng"}/daas`,
    "provider": {
      "@type": "Organization",
      "name": "TheSpire",
      "url": `${import.meta.env.VITE_SITE_URL || "https://thespire.com.ng"}`,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Abuja",
        "addressCountry": "Nigeria"
      }
    },
    "serviceType": "Educational Technology",
    "audience": {
      "@type": "Audience",
      "audienceType": "Educational Institutions"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Nigeria"
    },
    "offers": packages.map(pkg => ({
      "@type": "Offer",
      "name": pkg.name,
      "description": pkg.description,
      "price": pkg.price.replace('₦', ''),
      "priceCurrency": "NGN",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": pkg.price.replace('₦', ''),
        "priceCurrency": "NGN",
        "unitText": pkg.period
      }
    }))
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-hidden">
      <SEO
        title="Device-as-a-Service (DaaS) - Revolutionary School Technology | TheSpire"
        description="Transform education without breaking budgets. Revolutionary DaaS model providing complete tech infrastructure with support, paying per student."
        keywords="device as a service, revolutionary education, school technology transformation, educational technology, smartboards, VR learning, nigeria schools, edtech revolution"
        structuredData={structuredData}
      />
      
      {/* Dynamic Background */}
      <div 
        className="fixed inset-0 opacity-30 pointer-events-none"
        style={{
          background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(109, 3, 244, 0.3) 0%, rgba(0, 127, 255, 0.2) 25%, transparent 50%)`
        }}
      />
      
      {/* Floating Particles */}
      <div className="fixed inset-0 pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-[#6D03F4] rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-gradient-to-br from-[#6D03F4]/20 via-transparent to-[#007FFF]/20" />
        
        <div className="relative z-10 max-w-7xl mx-auto text-center">
          {/* Rotating Questions */}
          <div className="mb-8 h-16 flex items-center justify-center">
            <p className="text-lg md:text-xl text-[#6D03F4] font-medium transition-all duration-1000 transform">
              {questions[currentQuestion]}
            </p>
          </div>

          <h1 className="text-6xl md:text-8xl lg:text-9xl font-black mb-8 leading-none">
            <span className="block text-white">REVOLUTIONIZE</span>
            <span className="block bg-gradient-to-r from-[#6D03F4] to-[#007FFF] bg-clip-text text-transparent">
              EDUCATION
            </span>
          </h1>
          
          <p className="text-xl md:text-3xl text-gray-300 mb-12 max-w-4xl mx-auto font-light">
            Transform schools into <span className="text-[#6D03F4] font-bold">innovation hubs</span> without breaking budgets. 
            Pay per student, access the future.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Button size="lg" className="bg-gradient-to-r from-[#6D03F4] to-[#007FFF] hover:from-[#5A02D1] hover:to-[#0066CC] text-white px-12 py-4 text-lg font-bold transform hover:scale-105 transition-all duration-300 whitespace-nowrap">
              IGNITE TRANSFORMATION
            </Button>
            <Button 
              variant="outline" 
              size="lg" 
              className="border-2 border-[#6D03F4] text-[#6D03F4] hover:bg-[#6D03F4] hover:text-white px-12 py-4 text-lg font-bold transform hover:scale-105 transition-all duration-300 whitespace-nowrap"
            >
              EXPLORE REVOLUTION
            </Button>
          </div>
        </div>

        {/* Animated Grid */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(109, 3, 244, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(109, 3, 244, 0.3) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
            animation: 'grid-move 20s linear infinite'
          }} />
        </div>
      </section>

      {/* Revolutionary Benefits */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-7xl font-black text-white mb-6">
              WHY CHOOSE
              <span className="block bg-gradient-to-r from-[#6D03F4] to-[#007FFF] bg-clip-text text-transparent">
                REVOLUTION?
              </span>
            </h2>
            <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto">
              Transform your school into a <span className="text-[#6D03F4] font-bold">21st-century powerhouse</span> with our game-changing solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index} 
                className="group relative bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 hover:border-[#6D03F4]/50 transition-all duration-500 hover:transform hover:scale-105"
              >
                <div className={`w-16 h-16 bg-gradient-to-r ${benefit.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{benefit.title}</h3>
                <p className="text-gray-300">{benefit.description}</p>
                
                {/* Hover Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#6D03F4]/20 to-[#007FFF]/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Revolutionary Packages */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-7xl font-black text-white mb-6">
              CHOOSE YOUR
              <span className="block bg-gradient-to-r from-[#6D03F4] to-[#007FFF] bg-clip-text text-transparent">
                TRANSFORMATION
              </span>
            </h2>
            <p className="text-xl md:text-2xl text-gray-300">
              Revolutionary solutions designed for schools ready to <span className="text-[#6D03F4] font-bold">dominate the future</span>
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {packages.map((pkg, index) => (
              <div
                key={index}
                className={`relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl p-8 border transition-all duration-500 hover:transform hover:scale-105 ${
                  pkg.highlight 
                    ? 'border-[#6D03F4] shadow-2xl shadow-[#6D03F4]/25 scale-105' 
                    : 'border-white/20 hover:border-[#6D03F4]/50'
                }`}
              >
                {pkg.highlight && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-[#6D03F4] to-[#007FFF] text-white px-8 py-3 rounded-full text-sm font-bold uppercase tracking-wider">
                      MOST REVOLUTIONARY
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <div className="mb-4">
                    <span className={`inline-block px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider ${
                      pkg.highlight 
                        ? 'bg-gradient-to-r from-[#6D03F4] to-[#007FFF] text-white' 
                        : 'bg-white/20 text-[#6D03F4]'
                    }`}>
                      {pkg.intensity}
                    </span>
                  </div>
                  <h3 className="text-3xl font-black text-white mb-4">{pkg.name}</h3>
                  <div className="text-5xl font-black bg-gradient-to-r from-[#6D03F4] to-[#007FFF] bg-clip-text text-transparent mb-2">
                    {pkg.price}
                    <span className="text-lg text-gray-400 font-normal">/{pkg.period}</span>
                  </div>
                  <p className="text-gray-300 text-lg">{pkg.description}</p>
                </div>

                <div className="space-y-4 mb-8">
                  {pkg.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-gradient-to-r from-[#6D03F4] to-[#007FFF] rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <span className="text-gray-300 text-lg">{feature}</span>
                    </div>
                  ))}
                </div>

                <Button 
                  className={`w-full py-4 text-lg font-bold transform hover:scale-105 transition-all duration-300 whitespace-nowrap ${
                    pkg.highlight 
                      ? 'bg-gradient-to-r from-[#6D03F4] to-[#007FFF] hover:from-[#5A02D1] hover:to-[#0066CC]' 
                      : 'bg-white/20 hover:bg-gradient-to-r hover:from-[#6D03F4] hover:to-[#007FFF] text-white'
                  }`}
                  size="lg"
                >
                  START REVOLUTION
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Revolutionary Success Stories */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-7xl font-black text-white mb-6">
              SCHOOLS WE'VE
              <span className="block bg-gradient-to-r from-[#6D03F4] to-[#007FFF] bg-clip-text text-transparent">
                REVOLUTIONIZED
              </span>
            </h2>
            <p className="text-xl md:text-2xl text-gray-300">
              Real schools, <span className="text-[#6D03F4] font-bold">revolutionary results</span>, unstoppable impact
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {schools.map((school, index) => (
              <div key={index} className="group relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl overflow-hidden border border-white/20 hover:border-[#6D03F4]/50 transition-all duration-500 hover:transform hover:scale-105">
                <div className="relative">
                  <img
                    src={school.image}
                    alt={school.name}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute top-4 right-4 bg-gradient-to-r from-[#6D03F4] to-[#007FFF] px-4 py-2 rounded-full text-sm font-bold text-white">
                    {school.students} students
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-3 py-1 rounded-full text-xs font-bold text-white inline-block">
                      {school.transformation}
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-2">{school.name}</h3>
                  <p className="text-[#6D03F4] font-semibold mb-4">{school.location}</p>
                  <blockquote className="text-gray-300 italic text-lg">
                    "{school.testimonial}"
                  </blockquote>
                </div>
                
                {/* Hover Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#6D03F4]/20 to-[#007FFF]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Revolutionary CTA */}
      <section className="py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-[#6D03F4] to-[#007FFF] opacity-20" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl md:text-7xl font-black text-white mb-8">
            READY TO
            <span className="block bg-gradient-to-r from-[#6D03F4] to-[#007FFF] bg-clip-text text-transparent">
              REVOLUTIONIZE?
            </span>
          </h2>
          <p className="text-xl md:text-2xl text-gray-300 mb-12">
            Join the schools already <span className="text-[#6D03F4] font-bold">dominating the future</span> of education.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[#6D03F4] hover:bg-gray-100 px-12 py-4 text-lg font-bold transform hover:scale-105 transition-all duration-300 whitespace-nowrap">
                IGNITE TRANSFORMATION
              </Button>
            </Link>
            <Button variant="outline" size="lg" className="border-2 border-white text-white hover:bg-white hover:text-[#6D03F4] px-12 py-4 text-lg font-bold transform hover:scale-105 transition-all duration-300 whitespace-nowrap">
              SCHEDULE REVOLUTION
            </Button>
          </div>
        </div>
      </section>

      {/* WhatsApp Floating Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <a
          href="https://wa.me/2349167710173"
          target="_blank"
          rel="noopener noreferrer"
          className="w-14 h-14 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300 group"
        >
          <i className="ri-whatsapp-line text-white text-2xl group-hover:scale-110 transition-transform"></i>
        </a>
      </div>

      <style jsx>{`
        @keyframes grid-move {
          0% { transform: translate(0, 0); }
          100% { transform: translate(50px, 50px); }
        }
      `}</style>
    </div>
  );
}

